import type { MedplumClient } from '@medplum/core';
import type { Appointment, AppointmentParticipant, QuestionnaireResponse } from '@medplum/fhirtypes';
import { APPOINTMENT_TAG_SYSTEM, ATTENDANCE_SOURCE_EXT } from '@unify/fhir-model/constants';
import {
  setParticipantAttendanceStatus,
} from '@unify/fhir-model/resources/appointment';
import { getDetailedStatus } from '@unify/fhir-model/resources/questionnaire-response';
import {
  DEFAULT_PARTICIPANT_ATTENDANCE_STATUS,
  type ParticipantAttendanceStatus,
  getDefaultParticipantAttendanceFromAppointment,
  mergeParticipantAttendanceIntoResponseItems,
  resolveParticipantAttendanceFromAppointmentParticipant,
} from '@/pages/appointments/utils/participantAttendance';
import { normalizeCalendarClientAttendanceStatus } from '@/lib/calendarClientAttendanceStatus';

/** Zoom Meeting SDK client view layer. */
export const ZOOM_SDK_Z_INDEX = 9_999;
/** Unify header / sidebar chrome above Zoom. */
export const ZOOM_OVERLAY_CHROME_Z_INDEX = ZOOM_SDK_Z_INDEX + 1;
/** Mantine combobox dropdowns above Unify chrome. */
export const ZOOM_OVERLAY_COMBOBOX_Z_INDEX = ZOOM_SDK_Z_INDEX + 2;
/** Full-width dialogs (e.g. the session scheduler) above the sidebar chrome. */
export const ZOOM_OVERLAY_MODAL_Z_INDEX = ZOOM_SDK_Z_INDEX + 3;
/** Combobox dropdowns opened from inside those dialogs. */
export const ZOOM_OVERLAY_MODAL_COMBOBOX_Z_INDEX = ZOOM_SDK_Z_INDEX + 4;

const ATTENDANCE_SOURCE_SYSTEM = 'https://unify.health/fhir/CodeSystem/attendance-source';

function coerceSessionAttendanceStatus(
  status: ParticipantAttendanceStatus | string | null | undefined,
): ParticipantAttendanceStatus | null {
  if (status == null) return null;
  return normalizeCalendarClientAttendanceStatus(status) ?? DEFAULT_PARTICIPANT_ATTENDANCE_STATUS;
}

/** Read calendar attendance from participant extension (falls back to appointment context). */
export function readParticipantAppointmentAttendance(
  participant: AppointmentParticipant,
  appointment: Appointment,
): ParticipantAttendanceStatus {
  return resolveParticipantAttendanceFromAppointmentParticipant(participant, appointment);
}

export function applyParticipantAttendanceToParticipant(
  participant: AppointmentParticipant,
  status: ParticipantAttendanceStatus | null,
): AppointmentParticipant {
  const normalized = status != null ? coerceSessionAttendanceStatus(status) : null;
  const next: AppointmentParticipant = { ...participant };
  setParticipantAttendanceStatus(next, normalized ?? undefined);
  if (normalized === 'client-no-show') {
    next.status = 'declined';
  } else if (normalized) {
    next.status = 'accepted';
  }
  return next;
}

export function setAppointmentAttendanceSourceManual(appointment: Appointment): Appointment {
  const others = (appointment.extension ?? []).filter((e) => e.url !== ATTENDANCE_SOURCE_EXT);
  return {
    ...appointment,
    extension: [
      ...others,
      {
        url: ATTENDANCE_SOURCE_EXT,
        valueCoding: {
          system: ATTENDANCE_SOURCE_SYSTEM,
          code: 'manual',
          display: 'Manual (session sidebar)',
        },
      },
    ],
  };
}

export interface SessionAttendanceEntry {
  patientId: string;
  status: ParticipantAttendanceStatus | null;
}

export function readInitialAttendanceFromAppointment(
  appointment: Appointment,
): Map<string, ParticipantAttendanceStatus> {
  const fromAppointment = getDefaultParticipantAttendanceFromAppointment(appointment);
  return new Map(Object.entries(fromAppointment));
}

export function applyAttendanceEntriesToAppointment(
  appointment: Appointment,
  entries: SessionAttendanceEntry[],
): Appointment {
  const byPatient = new Map(entries.map((e) => [e.patientId, e.status]));
  let next: Appointment = {
    ...appointment,
    participant: (appointment.participant ?? []).map((p) => {
      const ref = p.actor?.reference ?? '';
      const patientId = ref.startsWith('Patient/') ? ref.replace('Patient/', '') : '';
      if (!patientId || !byPatient.has(patientId)) return p;
      return applyParticipantAttendanceToParticipant(p, byPatient.get(patientId) ?? null);
    }),
  };
  if (entries.some((e) => e.status != null)) {
    next = setAppointmentAttendanceSourceManual(next);
  }
  return next;
}

function attendanceRowsFromAppointment(appointment: Appointment): { patientId: string; displayName: string }[] {
  return (appointment.participant ?? [])
    .filter((p) => p.actor?.reference?.startsWith('Patient/'))
    .map((p) => {
      const patientId = p.actor!.reference!.replace('Patient/', '');
      return { patientId, displayName: p.actor?.display?.trim() || 'Client' };
    });
}

const LOCKED_NOTE_STATUSES = new Set(['signed', 'cosigned']);

function isLockedClinicalNote(qr: QuestionnaireResponse): boolean {
  const detailed = getDetailedStatus(qr);
  return detailed != null && LOCKED_NOTE_STATUSES.has(detailed);
}

/**
 * Persist attendance like the appointment progress note: Appointment.participant extension
 * plus participant-attendance on any appointment-tagged QuestionnaireResponse.
 */
export async function persistSessionAttendance(
  medplum: MedplumClient,
  appointment: Appointment,
  entries: SessionAttendanceEntry[],
): Promise<Appointment> {
  const updated = applyAttendanceEntriesToAppointment(appointment, entries);
  const savedAppt = await medplum.updateResource(updated);
  const normalizedAppt = applyAttendanceEntriesToAppointment(savedAppt, entries);

  const apptId = appointment.id;
  if (!apptId) return normalizedAppt;

  const byPatient: Record<string, ParticipantAttendanceStatus> = {};
  for (const e of entries) {
    const coerced = coerceSessionAttendanceStatus(e.status);
    if (coerced) byPatient[e.patientId] = coerced;
  }
  const rowMeta = attendanceRowsFromAppointment(normalizedAppt);
  if (rowMeta.length === 0) return normalizedAppt;

  const qrs = await medplum
    .searchResources('QuestionnaireResponse', {
      _tag: `${APPOINTMENT_TAG_SYSTEM}|${apptId}`,
      _count: '25',
    })
    .catch(() => []);

  await Promise.all(
    qrs.map(async (qr) => {
      if (!qr.id || isLockedClinicalNote(qr)) return;
      try {
        const item = mergeParticipantAttendanceIntoResponseItems(qr.item, byPatient, rowMeta);
        await medplum.updateResource({ ...qr, item });
      } catch {
        // Appointment.participant is the source of truth; note sync must not roll back that write.
      }
    }),
  );

  return normalizedAppt;
}
